import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Truck, 
  ArrowLeft,
  Plus,
  MessageSquare,
  LogOut,
  Send
} from "lucide-react";
import { toast } from "sonner";

export default function SupportTickets() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [orders, setOrders] = useState([]);
  
  // Modal states
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [showTicketDetails, setShowTicketDetails] = useState(null);
  
  // Form states
  const [newTicket, setNewTicket] = useState({ subject: "", message: "", order_id: "" });
  const [replyText, setReplyText] = useState("");

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ticketsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/tickets`, { headers }),
        axios.get(`${API}/orders`, { headers })
      ]);
      
      setTickets(ticketsRes.data);
      setOrders(ordersRes.data);
    } catch (e) {
      console.error("Error fetching data:", e);
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.subject || !newTicket.message) {
      toast.error("Bitte Betreff und Nachricht ausfüllen");
      return;
    }
    
    try {
      await axios.post(`${API}/tickets`, {
        subject: newTicket.subject,
        message: newTicket.message,
        order_id: newTicket.order_id || null
      }, { headers });
      
      toast.success("Ticket erstellt");
      setShowCreateTicket(false);
      setNewTicket({ subject: "", message: "", order_id: "" });
      fetchData();
    } catch (e) {
      toast.error("Fehler beim Erstellen");
    }
  };

  const handleReply = async () => {
    if (!replyText.trim()) {
      toast.error("Bitte Nachricht eingeben");
      return;
    }
    
    try {
      await axios.post(`${API}/tickets/${showTicketDetails.id}/reply`, {
        message: replyText
      }, { headers });
      
      toast.success("Antwort gesendet");
      setReplyText("");
      
      // Refresh ticket
      const response = await axios.get(`${API}/tickets/${showTicketDetails.id}`, { headers });
      setShowTicketDetails(response.data);
      fetchData();
    } catch (e) {
      toast.error("Fehler beim Senden");
    }
  };

  const handleOpenTicket = async (ticket) => {
    setShowTicketDetails(ticket);
    // Mark as read when opening
    try {
      await axios.post(`${API}/tickets/${ticket.id}/mark-read`, {}, { headers });
    } catch (e) {
      // Silently fail
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <Truck className="h-6 w-6 text-white" />
              </div>
              <div>
                <span className="font-bold text-lg">SonderCargo.de</span>
                <span className="text-xs text-slate-400 block">Medical, Weapons and much more</span>
              </div>
            </div>
            
            <nav className="hidden md:flex items-center gap-6">
              <Link 
                to={user?.role === "admin" || user?.role === "support" ? "/admin" : "/dashboard"} 
                className="text-slate-300 hover:text-white transition"
                data-testid="nav-dashboard"
              >
                Dashboard
              </Link>
              <Link to="/support" className="text-white font-medium" data-testid="nav-support">
                Support
              </Link>
            </nav>
            
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="font-medium">{user?.name}</p>
                <p className="text-xs text-slate-400 uppercase">{user?.role}</p>
              </div>
              <Button variant="ghost" size="icon" className="text-slate-300 hover:text-white" onClick={logout} data-testid="logout-btn">
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="h-1 bg-gradient-to-r from-blue-600 via-blue-500 to-blue-400" />

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link 
          to={user?.role === "admin" || user?.role === "support" ? "/admin" : "/dashboard"} 
          className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-6"
          data-testid="back-link"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Zurück zum Dashboard
        </Link>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" data-testid="page-title">Support</h1>
            <p className="text-slate-500">Ihre Tickets und Anfragen</p>
          </div>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setShowCreateTicket(true)} data-testid="create-ticket-btn">
            <Plus className="h-4 w-4 mr-2" />
            Neues Ticket
          </Button>
        </div>

        {/* Tickets List */}
        <div className="space-y-4">
          {tickets.map(ticket => (
            <Card 
              key={ticket.id} 
              className="border-0 shadow-sm hover:shadow-md transition cursor-pointer"
              onClick={() => handleOpenTicket(ticket)}
              data-testid={`ticket-${ticket.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      ticket.status === "OPEN" ? "bg-red-100" : "bg-green-100"
                    }`}>
                      <MessageSquare className={`h-6 w-6 ${
                        ticket.status === "OPEN" ? "text-red-600" : "text-green-600"
                      }`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{ticket.subject}</h3>
                      <p className="text-sm text-slate-500 mt-1">
                        {ticket.order_number ? `Bezug: ${ticket.order_number}` : "Allgemeine Anfrage"}
                      </p>
                      <p className="text-xs text-slate-400 mt-2">
                        Erstellt am {ticket.created_at?.split("T")[0]} • 
                        {ticket.messages?.length || 1} Nachricht(en)
                      </p>
                    </div>
                  </div>
                  <Badge className={ticket.status === "OPEN" ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}>
                    {ticket.status}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
          
          {tickets.length === 0 && (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-12 text-center">
                <MessageSquare className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                <h3 className="font-semibold text-slate-900 mb-2">Keine Tickets vorhanden</h3>
                <p className="text-slate-500 mb-4">Sie haben noch keine Support-Anfragen erstellt.</p>
                <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setShowCreateTicket(true)} data-testid="empty-create-ticket">
                  Erstes Ticket erstellen
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Create Ticket Modal */}
      <Dialog open={showCreateTicket} onOpenChange={setShowCreateTicket}>
        <DialogContent className="sm:max-w-lg" data-testid="create-ticket-modal">
          <DialogHeader>
            <DialogTitle>Neues Support-Ticket</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Betreff *</Label>
              <Input 
                value={newTicket.subject}
                onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                placeholder="Kurze Beschreibung des Problems"
                data-testid="ticket-subject"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Bezug auf Sendung (optional)</Label>
              <Select 
                value={newTicket.order_id || "none"} 
                onValueChange={(v) => setNewTicket({ ...newTicket, order_id: v === "none" ? "" : v })}
              >
                <SelectTrigger data-testid="ticket-order">
                  <SelectValue placeholder="Sendung auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Keine Sendung</SelectItem>
                  {orders.map(order => (
                    <SelectItem key={order.id} value={order.id}>
                      {order.order_number} - {order.receiver_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Nachricht *</Label>
              <Textarea 
                value={newTicket.message}
                onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
                placeholder="Beschreiben Sie Ihr Anliegen..."
                rows={5}
                data-testid="ticket-message"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateTicket(false)}>Abbrechen</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleCreateTicket} data-testid="submit-ticket">
              Ticket erstellen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ticket Details Modal */}
      <Dialog open={!!showTicketDetails} onOpenChange={() => setShowTicketDetails(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] flex flex-col" data-testid="ticket-details-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{showTicketDetails?.subject}</span>
              <Badge className={showTicketDetails?.status === "OPEN" ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800"}>
                {showTicketDetails?.status}
              </Badge>
            </DialogTitle>
          </DialogHeader>
          
          {showTicketDetails && (
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="text-sm text-slate-500 mb-4">
                {showTicketDetails.order_number && (
                  <p>Sendung: {showTicketDetails.order_number}</p>
                )}
                <p>Erstellt: {showTicketDetails.created_at?.split("T")[0]}</p>
              </div>
              
              {/* Messages */}
              <div className="flex-1 overflow-y-auto space-y-4 mb-4 max-h-80">
                {showTicketDetails.messages?.map((msg, idx) => (
                  <div 
                    key={idx} 
                    className={`p-4 rounded-lg ${
                      msg.user_id === user?.id 
                        ? "bg-blue-50 ml-8" 
                        : "bg-slate-50 mr-8"
                    }`}
                    data-testid={`message-${idx}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-sm">{msg.user_name}</span>
                      <span className="text-xs text-slate-400">{msg.timestamp?.split("T")[0]}</span>
                    </div>
                    <p className="text-sm text-slate-700">{msg.message}</p>
                  </div>
                ))}
              </div>
              
              {/* Reply */}
              {showTicketDetails.status === "OPEN" && (
                <div className="border-t pt-4">
                  <div className="flex gap-2">
                    <Textarea 
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Antwort schreiben..."
                      rows={2}
                      className="flex-1"
                      data-testid="reply-input"
                    />
                    <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleReply} data-testid="send-reply">
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
